/** @jsx jsx */

import React, {
  useState,
  useRef,
  useContext,
  useEffect,
  useLayoutEffect,
} from 'react';
import { css, jsx } from '@emotion/core';
import { interactionContext } from './interactionContext';
import checkReturnToInteractionEditing from 'services/checkReturnToInteractionEditing';
import Face from './Face';
import { getButtonFocus } from 'styles/variables';

const Flipcard = props => {
  const { id, updateFlipCardSeen, children } = props;
  // const [rotation, setRotation] = useState(0);
  // const [visibleFace, setVisibleFace] = useState('all');

  const [cardHasFocus, setCardHasFocus] = useState(false);
  const [{ rotation, visibleFace }, updateCardDisplay] = useState({
    rotation: 0,
    visibleFace: 'front',
  });

  const [edgeOpacityHack, setEdgeOpacityHack] = useState(0);
  const context = useContext(interactionContext);
  const {
    flipcardFrontColor,
    flipcardBackColor,
    flipcardFrontTextColor,
    flipcardBackTextColor,
    focusColor,
    flipcardWidth,
    flipcardHeight,
    alignTextTop,
  } = context.props;

  const flipCard = useRef(null);
  const flipCardFaces = useRef(undefined);
  const flipCardInit = useRef(false);
  const hiddenButton = useRef(null);

  const updateRotation = e => {
    //
    window.dev() && checkReturnToInteractionEditing(true);

    // const { left, width } = e.currentTarget.getBoundingClientRect();
    // const side = e.clientX < left + width / 2 ? 'left' : 'right';

    let bounds;
    let left;
    let width;
    let side = 'left';

    if (e) {
      bounds = e.currentTarget.getBoundingClientRect();
      left = bounds.left;
      width = bounds.width;
      side = e.clientX < left + width / 2 ? 'left' : 'right';
    }

    // setVisibleFace('all');
    // setRotation(rotation + (side === 'left' ? -180 : 180));

    updateCardDisplay({
      rotation: rotation + (side === 'left' ? -180 : 180),
      visibleFace: 'all',
    });

    setEdgeOpacityHack(edgeOpacityHack => (edgeOpacityHack === 0 ? 1 : 0));

    updateFlipCardSeen(id);
  };

  const cardWithFocusId = useRef(null);

  const flipCardBaseShadow = '0 8px 24px 5px rgba(0, 0, 0, 0.2)';

  const getFocusColor = () => {
    if (focusColor) {
      return focusColor;
    }

    return (Math.abs(rotation) / 180) % 2 == 0
      ? ['#fff', '#ffffff', 'rgb(255, 255, 255)'].includes(flipcardFrontColor)
        ? flipcardBackColor
        : flipcardFrontColor
      : flipcardBackColor;
  };

  const getFocusColor_2 = () =>
    getButtonFocus(getFocusColor(), flipCardBaseShadow)
      .replace(/box-shadow:|;|!important/g, '')
      .trim();

  useEffect(() => {
    if (!flipCardFaces.current) {
      flipCardFaces.current = Array.from(flipCard.current.children);
    }

    const onTransitionEnd = e => {
      const visibleFace =
        (Math.abs(rotation) / 180) % 2 === 1 ? 'back' : 'front';

      // if we want to NOT use state. . .
      const hiddenCardElement: any = flipCardFaces.current.filter(element =>
        element.id.includes(visibleFace)
      )[0];

      if (flipCardInit.current) {
        updateCardDisplay({
          rotation,
          visibleFace,
        });
        flipCard.current.removeEventListener('transitionend', onTransitionEnd);
      }

      flipCardInit.current = true;
      // flipCard.current.removeEventListener('transitionend', onTransitionEnd);
    };

    flipCard.current.addEventListener('transitionend', onTransitionEnd);

    return () => {
      flipCard.current.removeEventListener('transitionend', onTransitionEnd);
    };
  }, [rotation]);

  useEffect(() => {
    if (window.dev()) {
      return;
    }

    const padding = 60;

    const checkFlipCardSize = () => {
      if (window.innerWidth - padding <= flipcardWidth) {
        let flipCardResizeRatio = window.innerWidth / flipcardWidth;

        flipCard.current.style.width = `${flipcardWidth * flipCardResizeRatio -
          padding}px`;
        flipCard.current.style.height = `${flipcardHeight *
          flipCardResizeRatio -
          padding}px`;

        return;
      }

      if (flipCard.current.style.width === `${flipcardWidth}px`) {
        return;
      }

      flipCard.current.style.width = `${flipcardWidth}px`;
      flipCard.current.style.height = `${flipcardHeight}px`;
    };

    checkFlipCardSize();

    window.addEventListener('resize', checkFlipCardSize);

    return () => {
      window.removeEventListener('resize', checkFlipCardSize);
    };
  }, []);

  return (
    <div
      ref={flipCard}
      id={`i${id}`}
      css={css`
        // kill the btn styles. . .
        // outline: none;
        // border: none;
        background: transparent;
        border-radius: 10px;
        //
        position: relative;
        width: ${flipcardWidth}px;
        height: ${flipcardHeight}px;
        margin: 10px;
        perspective: 1000px;
        cursor: pointer;

        ${cardHasFocus &&
          `
        #${visibleFace}-${id} {
          ${getButtonFocus(
            getFocusColor(),
            '0 8px 24px 5px rgba(0, 0, 0, 0.2)'
          )}
        }
        `} // list-style-type: none;
      `}
      // onKeyUp={e => {
      //   if (![' ', 'Enter'].includes(e.key)) {
      //     return;
      //   }

      //   updateRotation(e);
      // }}
      onClick={(e: any) => {
        // if (window.dev()) {
        //   if (e.detail === 0) {
        //     return;
        //   }
        // }

        e.stopPropagation();
        updateRotation(e);

        if (hiddenButton.current) {
          requestAnimationFrame(() => hiddenButton.current.focus());
        }
      }}
      aria-label="flip card"
      role="list-item"
      // tabIndex={0}
    >
      {!window.dev() && (
        <button
          ref={hiddenButton}
          css={css`
            position: absolute;
            left: 0;
            top: 0;
            width: 0;
            height: 0;
            border: 0;
            outline: none;
            overflow: hidden;
          `}
          onFocus={e => {
            setCardHasFocus(true);
          }}
          onBlur={() => {
            setCardHasFocus(false);
          }}
          onClick={e => {
            e?.stopPropagation();
            updateRotation(null);
          }}
          aria-pressed={visibleFace !== 'front'}
        />
      )}

      <Face
        id={`front-${id}`}
        face="front"
        color={flipcardFrontColor}
        textColor={flipcardFrontTextColor}
        alignTextTop={alignTextTop}
        zIndex={1}
        transform={`rotateY(${rotation}deg)`}
        {...{
          opacity: edgeOpacityHack === 1,
        }}
        visibleFace={visibleFace}
        visibility={['front', 'all'].includes(visibleFace)}
      >
        {children[0]}
      </Face>
      <Face
        id={`back-${id}`}
        face="back"
        color={flipcardBackColor}
        textColor={flipcardBackTextColor}
        alignTextTop={alignTextTop}
        zIndex={2}
        transform={`rotateY(${rotation + 180}deg)`}
        {...{
          opacity: edgeOpacityHack === 0,
        }}
        visibleFace={visibleFace}
        visibility={['back', 'all'].includes(visibleFace)}
      >
        {children[1]}
      </Face>
    </div>
  );
};

export default Flipcard;
